// ─── EVENT ASSIGNMENT MATCHING ────────────────────────────────────────────────
// Who an event belongs to, resolved the same way everywhere:
//   1. the event lives on one of the person's mapped calendars, OR
//   2. the FOH metadata block names them as assignee, OR
//   3. the title carries their "(ID)" suffix tag.

import { parseFOHMeta } from './rules';
// lib/gcal + lib/firebase are dynamically imported inside setEventAssignee
// (below), not statically here — a static import would pull firebase/auth
// into every consumer of this module, including its own pure-function tests,
// which the test environment can't load (missing TextEncoder).

const TITLE_TAG = /\(([A-Z][A-Z\-/]{0,5})\)\s*$/;
// Same shape as TITLE_TAG (dash/slash allowed, up to 6 chars) so anything
// getEventAssignee can READ, stripAssigneeTag can also REMOVE — the two were
// previously out of sync (this one used to reject "-"/"/" and cap at 4 chars),
// which let tags like "(G/D)" survive stripping and accumulate as duplicates.
const TRAILING_TAG = /\s*\([A-Z][A-Z\-/]{0,5}\)\s*$/;

// Strips every trailing (ID) tag, not just one — a title can accumulate
// several duplicates if it's been re-tagged multiple times.
export function stripAssigneeTag(title) {
  let t = (title || '').trim();
  while (TRAILING_TAG.test(t)) t = t.replace(TRAILING_TAG, '').trim();
  return t;
}

// Assignee identifier for an event, or null. Uses a pre-parsed event.fohMeta
// when present (Review & Assign events), otherwise parses the description.
export function getEventAssignee(event) {
  if (!event) return null;
  const meta = event.fohMeta !== undefined ? event.fohMeta : parseFOHMeta(event.description);
  if (meta?.assignee) return meta.assignee;
  const m = (event.summary || '').match(TITLE_TAG);
  return m ? m[1] : null;
}

export function isAssignedTo(event, { identifier = null, calIds = [] } = {}) {
  if (!event) return false;
  const calId = event._calendarId || event.calendarId;
  if (calId && calIds.includes(calId)) return true;
  if (!identifier) return false;
  return getEventAssignee(event) === identifier;
}

// ─── EVENT ASSIGNMENT WRITES ──────────────────────────────────────────────────
// Write counterpart to the read helpers above. Same FOH-block format the
// Logistics "Lock In" flow writes, exported so other callers (e.g. a quick
// "running late" unassign from the Today view) don't need to duplicate it.

// Removes the "-- Managed by ... --" metadata block (any app-name era) from a
// GCal event description, leaving just the human-authored notes.
export function stripFohMeta(desc) {
  return (desc || '').replace(/-- Managed by \S+ --[\s\S]*?last updated:.*?(\n|$)/g, '').trim();
}

// `session` links this event (or, for a recurring booking, this specific
// instance) to a families/{id}/sessions/{id} doc — the durable pointer the
// caregiver-decline flow uses to find its way back to a calendar event from
// Firestore. Not auto-preserved across writes — a caller that wants to keep
// an existing session link through an otherwise-unrelated write (e.g. Lock
// In re-assigning the event) must pass it explicitly, same as every other
// field here.
export function buildFOHDescription(existingDesc, meta) {
  const today = new Date().toISOString().split('T')[0];
  const sessionSeg = meta.session ? `  |  session: ${meta.session}` : '';
  const block = [
    '-- Managed by Grove --',
    `assignee: ${meta.assignee || 'none'}  |  reminder: ${meta.reminder ? 'yes' : 'no'}  |  reviewed: ${meta.reviewed ? 'yes' : 'no'}${sessionSeg}`,
    `created: ${today}  |  last updated: ${today}`,
  ].join('\n');

  // Strip any existing FOH block and append new one
  const stripped = stripFohMeta(existingDesc);
  return stripped ? `${stripped}\n\n${block}` : block;
}

// Sets (or clears, with assignee=null) who a GCal-tagged event belongs to —
// mirrors the Lock In write path (GCal PATCH + Firestore changelog) exactly,
// so any caller sees the same result the Logistics screen would produce.
// Always patches the single instance (event.id), never a recurring series.
// `session` carries a sessions-doc link through the write (see
// buildFOHDescription). `titleSuffix` appends fixed text after the base
// title when clearing the assignee (e.g. " (Declined)") — only meaningful
// when assignee is falsy, since an assigned title already ends in "(ID)".
// `familyId` omitted (as when a token-based caregiver's own action reconciles
// later from a parent's session) skips the changelog write, since there's no
// authenticated actor to attribute it to.
export async function setEventAssignee(event, assignee, { actorId, actorName, familyId, reason, session, titleSuffix } = {}) {
  const { patchEvent: gcalPatchEvent } = await import('../lib/gcal');
  const { fsLogChange } = await import('../lib/firebase');

  const existingDesc = event._raw?.description ?? event.description ?? '';
  const newDesc = buildFOHDescription(existingDesc, {
    assignee, reviewed: !!assignee, session,
  });

  const baseTitle = stripAssigneeTag(event.summary || '');
  const newTitle = assignee ? `${baseTitle} (${assignee})` : `${baseTitle}${titleSuffix || ''}`;

  await gcalPatchEvent(event._calendarId, event.id, {
    summary: newTitle,
    description: newDesc,
  });

  if (familyId) {
    await fsLogChange(familyId, {
      actorId, actorName,
      module: 'home',
      action: 'assign_event',
      entityType: 'event',
      entityId: event.id,
      fromValue: getEventAssignee(event),
      toValue: assignee ? `${assignee} — ${baseTitle}` : `none — ${baseTitle}${reason ? ` (${reason})` : ''}`,
    });
  }
}
